import { dialogClasses } from "@mui/material/Dialog";
import { switchClasses } from "@mui/material/Switch";
import { Theme, ThemeOptions } from "@mui/material";

export const darkModePalette: ThemeOptions["palette"] = {
  mode: "dark",
  iconColor: {
    main: "#FFFFFF",
  },
  primary: {
    light: "#4b9f9c",
    main: "#137F7B",
    dark: "#0B504C",
    contrastText: "rgb(255, 255, 255)",
  },
  secondary: {
    light: "#017b88",
    main: "#015b58",
    dark: "#013b39",
    contrastText: "rgb(255, 255, 255)",
  },
  buttonGrey: {
    main: "#e0e0e0",
    dark: "#d5d5d5",
    contrastText: "rgba(0, 0, 0, 0.86)",
  },
  buttonText: {
    main: "#f4f4f4",
  },
  text: {
    primary: "#f4f4f4",
    secondary: "#F4F4F4",
    light: "#999999",
    contrast: "#333333",
  },
  background: {
    default: "#000000",
    contrast: "#f4f4f4",
    medium: "#666666",
    light: "#333333",
  },
};

export const lightModePalette: ThemeOptions["palette"] = {
  mode: "light",
  iconColor: {
    main: "#333333",
  },
  primary: {
    light: "#4b9f9c",
    main: "#137f7b",
    dark: "#086662",
    contrastText: "rgb(255, 255, 255)",
  },
  secondary: {
    light: "#017b88",
    main: "#015b58",
    dark: "#013b39",
    contrastText: "rgb(255, 255, 255)",
  },
  buttonGrey: {
    main: "#e0e0e0",
    dark: "#d5d5d5",
    contrastText: "rgba(0, 0, 0, 0.86)",
  },
  buttonText: {
    main: "#272d4e",
  },
  text: {
    primary: "#272d4e",
    secondary: "#666666",
    light: "#999999",
    contrast: "#d0e7e6",
  },
  background: {
    default: "#fff",
    contrast: "#f7f7f7",
    medium: "#666666",
    light: "#f7f7f7",
  },
};

export const getBaseThemeOptions = (theme: Theme): ThemeOptions => ({
  typography: {
    fontFamily: ["Roboto", "Helvetica", "Arial", "sans-serif"].join(","),
    allVariants: {
      lineHeight: "1.5",
    },
    h1: {
      fontSize: "3.5rem",
      fontWeight: 500,
      marginTop: "1.25rem",
      marginBottom: "0.625rem",
      [theme.breakpoints.down("sm")]: { fontSize: "2rem" },
    },
    h2: {
      fontSize: "3rem",
      fontWeight: 500,
      marginTop: "1.25rem",
      marginBottom: "0.625rem",
      [theme.breakpoints.down("sm")]: { fontSize: "1.75rem" },
    },
    h3: {
      fontSize: "2.5rem",
      fontWeight: 500,
      marginTop: "1.25rem",
      marginBottom: "0.625rem",
      [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
    },
    h4: {
      fontSize: "1.75rem",
      fontWeight: 500,
      marginTop: "0.625rem",
      marginBottom: "0.625rem",
      [theme.breakpoints.down("sm")]: { fontSize: "1.25rem" },
    },
    h5: {
      fontSize: "1.5rem",
      fontWeight: 500,
      marginTop: "0.625rem",
      marginBottom: "0.625rem",
      [theme.breakpoints.down("sm")]: { fontSize: "1.125rem" },
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 500,
      [theme.breakpoints.down("sm")]: { fontSize: "1rem" },
    },
    subtitle1: {
      fontWeight: 300,
      fontSize: "1.75rem",
      [theme.breakpoints.down("sm")]: { fontSize: "1.25rem" },
    },
    subtitle2: {
      fontWeight: 300,
      fontSize: "1.25rem",
      [theme.breakpoints.down("sm")]: { fontSize: "1.125rem" },
    },
    body1: {
      fontWeight: 300,
      fontSize: "1rem",
    },
    body2: {
      lineHeight: "1.3rem",
      fontWeight: 300,
      fontSize: ".75rem",
    },
    button: {
      fontSize: "1rem",
      fontWeight: 400,
    },
  },
  components: {
    MuiPickerYear: {
      styleOverrides: {
        yearButton: {
          fontSize: "1rem",
          fontWeight: 400,
        },
      },
    },
    MuiPickerDay: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          "&.MuiPickerDay-today:not(.Mui-selected)": {
            outline: "none",
            border: `1px dashed ${theme.palette.text.light}`,
            backgroundColor: "transparent",
          },
        }),
      },
    },
    MuiTimePickerToolbar: {
      styleOverrides: {
        separator: ({ theme }: { theme: Theme }) => theme.typography.h3,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: "8px",
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          lineHeight: "inherit",
        },
      },
      defaultProps: {
        required: false,
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: "0.875rem",
          fontWeight: 400,
          lineHeight: 1.5,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          margin: 24,
        },
        root: {
          [`& .${dialogClasses.scrollPaper} > .${dialogClasses.paper}`]: {
            maxHeight: "calc(100% - 48px)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 25,
        },
      },
      defaultProps: {
        elevation: 8,
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 8,
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          textAlign: "center",
          "& .MuiTypography-h6": {
            color: "#6ce9e4",
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          [`& .${switchClasses.colorPrimary}.${switchClasses.disabled}`]: {
            color: "#bcdada",
            [`& + .${switchClasses.track}`]: {
              backgroundColor: "#137f7b",
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& input:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 100px #E8F0FE inset",
            WebkitTextFillColor: "#000",
            transition: "background-color 5000s ease-in-out 0s",
          },
        },
      },
    },
  },
});
